import { isUnusableLifecycleStatus, type LifecycleStatus } from './lifecycleStatus'
import { toWallClockString } from '@/shared/lib/tenantDateTime'

/**
 * Legacy `lotsExpireAlert` parity (JW 407): calendar-day dateDiff windows in the site TZ.
 *
 * | dateDiff | kind |
 * |----------|------|
 * | 8        | week |
 * | 0        | today |
 * | 1        | day |
 * | 2–7      | days (N = dateDiff) |
 * | else / unusable lifecycle | null |
 */
export type LotsExpireAlert =
  | { kind: 'week' }
  | { kind: 'today' }
  | { kind: 'day' }
  | { kind: 'days'; count: number }

const parseDateOnlyUtcMs = (dateOnlyString: string): number | null => {
  const match = dateOnlyString.slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!match) return null
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  if (!year || !month || !day) return null
  return Date.UTC(year, month - 1, day)
}

/** Calendar-day difference: expiration − today, both as YYYY-MM-DD in `timeZone`. */
export const calendarDaysUntilExpiration = (
  expirationDate: string,
  timeZone: string,
  now: Date = new Date(),
): number | null => {
  const expirationMs = parseDateOnlyUtcMs(expirationDate)
  if (expirationMs == null) return null

  const todayDateOnly = toWallClockString(now, timeZone).slice(0, 10)
  const todayMs = parseDateOnlyUtcMs(todayDateOnly)
  if (todayMs == null) return null

  return Math.round((expirationMs - todayMs) / 86_400_000)
}

export const getLotsExpireAlert = ({
  expirationDate,
  lifecycleStatus,
  timeZone,
  now = new Date(),
}: {
  expirationDate: string | null | undefined
  lifecycleStatus: LifecycleStatus | null | undefined
  timeZone: string
  now?: Date
}): LotsExpireAlert | null => {
  if (!expirationDate || !timeZone) return null
  if (isUnusableLifecycleStatus(lifecycleStatus)) return null

  const dateDiff = calendarDaysUntilExpiration(expirationDate, timeZone, now)
  if (dateDiff == null) return null

  if (dateDiff === 8) return { kind: 'week' }
  if (dateDiff === 0) return { kind: 'today' }
  if (dateDiff === 1) return { kind: 'day' }
  if (dateDiff >= 2 && dateDiff <= 7) return { kind: 'days', count: dateDiff }
  return null
}
